/**
 * Analytics Page - Comprehensive cross-brand analytics view
 * Displays detailed performance metrics, trends, and breakdowns
 */

import React, { useState, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Tabs,
  Button,
  Grid,
  Spinner,
  Banner,
  Divider,
  Icon,
  Tooltip,
  TextField,
  Modal,
  FormLayout,
  Popover,
  ActionList,
  ChoiceList,
} from '@shopify/polaris';
import {
  CashDollarIcon,
  CashDollarFilledIcon,
  OrderIcon,
  StoreIcon,
  ChartVerticalIcon,
  CalendarIcon,
  FilterIcon,
  ReceiptRefundIcon,
  ReturnIcon,
} from '@shopify/polaris-icons';

// Import analytics components
import MetricCard, { RevenueCard, CommissionCard, OrdersCard } from '../components/Analytics/MetricCard';
import MarketplaceBreakdown from '../components/Analytics/MarketplaceBreakdown';
import ConnectorPerformance from '../components/Analytics/ConnectorPerformance';
import { useBrands, useAnalyticsOverview, useDetailedAnalytics } from '../utils/queries';
import apiClient from '../utils/api';

// Helper function - formats date as YYYY-MM-DD without timezone shift
// This avoids the bug where toISOString() converts to UTC, shifting dates by 1 day
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Analytics() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeFilter, setTimeFilter] = useState('30');
  // A list, not a string. Empty means every brand, matching the API's contract
  // where an absent or empty `brand` param applies no restriction.
  const [brandFilter, setBrandFilter] = useState([]);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);

  // Custom date range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [periodPopoverActive, setPeriodPopoverActive] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customDateRange, setCustomDateRange] = useState(null);

  // Query hooks for data fetching with safe data access
  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands();
  const brands = useMemo(() => {
    try {
      if (Array.isArray(brandsResponse)) {
        return brandsResponse.filter(brand => brand && brand.id);
      }
      if (brandsResponse && Array.isArray(brandsResponse.brands)) {
        return brandsResponse.brands.filter(brand => brand && brand.id);
      }
      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error processing brands data:', error);
      }
      return [];
    }
  }, [brandsResponse]);

  /**
   * Total brand count as the API reports it.
   *
   * `brands` is one page of GET /brands (useBrands() is called with no params, so the server's
   * default page size applies) and the subtitle read `brands.length` - a page length presented
   * as "insights across N brands". The response carries pagination.totalBrands
   * (brands.controller.js:102-106); fall back to the page length only when it is absent.
   */
  const totalBrandCount = useMemo(() => {
    const reported = brandsResponse?.pagination?.totalBrands;
    return Number.isFinite(Number(reported)) ? Number(reported) : brands.length;
  }, [brandsResponse, brands.length]);

  /**
   * NOTE: useAnalyticsOverview() is still fetched because the page gates its loading and error
   * states on it, but `analyticsOverview` itself is not rendered anywhere - every card on this
   * page reads useDetailedAnalytics instead. Left in place deliberately: it is the only call that
   * proves the analytics API is reachable before the page commits to rendering, and removing it
   * would silently drop that check. Flagged rather than changed.
   */
  const {
    data: analyticsOverview,
    isLoading: overviewLoading,
    error: overviewError
  } = useAnalyticsOverview();
  // Build analytics query parameters
  const analyticsParams = useMemo(() => {
    // Comma-separated; parseBrandIds on the server splits it. 'all' keeps the
    // previous wire format for an empty selection rather than omitting the key.
    const params = { brand: brandFilter.length ? brandFilter.join(',') : 'all' };

    if (timeFilter === 'custom' && customDateRange) {
      // For custom date range, pass actual dates
      params.startDate = customDateRange.startDate;
      params.endDate = customDateRange.endDate;

      // Also calculate days for backward compatibility
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      const diffTime = Math.abs(end - start);
      params.days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } else {
      // Handle preset date ranges
      const now = new Date();
      switch (timeFilter) {
        case 'mtd': // Month to Date
          params.startDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
          params.endDate = formatDateLocal(now);
          params.days = now.getDate();
          break;
        case 'ytd': // Year to Date
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          params.startDate = formatDateLocal(startOfYear);
          params.endDate = formatDateLocal(now);
          params.days = Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24));
          break;
        case 'this_month': // This Month
          params.startDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
          params.endDate = formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
          params.days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          break;
        case 'last_month': // Last Month
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
          params.startDate = formatDateLocal(lastMonth);
          params.endDate = formatDateLocal(lastMonthEnd);
          params.days = lastMonthEnd.getDate();
          break;
        default:
          // For simple day counts, calculate start date
          const parsed = parseInt(timeFilter, 10);
          const daysCount = isNaN(parsed) || parsed <= 0 ? 30 : Math.min(parsed, 999999);
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - daysCount);
          params.startDate = formatDateLocal(startDate);
          params.endDate = formatDateLocal(now);
          params.days = daysCount;
      }
    }

    return params;
  }, [brandFilter, timeFilter, customDateRange]);

  const {
    data: detailedAnalytics,
    isLoading: detailedLoading,
    error: detailedError
  } = useDetailedAnalytics(analyticsParams);

  // Filter options
  const timeFilterOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Month to Date', value: 'mtd' },
    { label: 'Year to Date', value: 'ytd' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'Last 365 days', value: '365' },
    // No 'custom' entry: the menu appends its own row, which shows the applied range as its
    // label and opens the picker. Leaving one here meant the list had to be filtered at
    // every use, and a caller that forgot would offer a row doing nothing.
  ];

  // No 'All Brands' entry: with checkboxes, all-brands IS the empty selection.
  const brandFilterOptions = useMemo(() => {
    const options = [];
    if (Array.isArray(brands)) {
      brands.forEach(brand => {
        if (brand && brand.id && brand.name) {
          options.push({
            label: brand.name,
            value: brand.id
          });
        }
      });
    }
    return options;
  }, [brands]);

  // Custom date range functions
  const handleTimeFilterChange = (value) => {
    if (value === 'custom') {
      // The menu opens the picker directly now, so 'custom' only ever arrives here from a
      // stale caller. Explicitly inert: falling through would call setTimeFilter('custom')
      // and re-run the query with no range attached.
    } else {
      setTimeFilter(value);
      setCustomDateRange(null);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);

      if (startDate <= endDate) {
        setCustomDateRange({
          startDate: customStartDate,
          endDate: customEndDate
        });
        setTimeFilter('custom');
        setShowCustomDateModal(false);
      }
    }
  };

  const formatCustomDateLabel = () => {
    if (customDateRange) {
      const startDate = new Date(customDateRange.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const endDate = new Date(customDateRange.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `${startDate} - ${endDate}`;
    }
    return 'Custom Date Range';
  };

  // Calculate performance metrics with error boundaries
  const performanceMetrics = useMemo(() => {
    try {
      if (!detailedAnalytics || typeof detailedAnalytics !== 'object') return null;

      return detailedAnalytics;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error processing performance metrics:', error);
      }
      return null;
    }
  }, [detailedAnalytics]);

  // Tab configuration
  const tabs = [
    {
      id: 'overview',
      content: 'Performance Overview',
      panelID: 'overview-panel',
      accessibilityLabel: 'View performance overview and key metrics'
    },
    {
      id: 'marketplace',
      content: 'Marketplace Analysis',
      panelID: 'marketplace-panel',
      accessibilityLabel: 'View marketplace performance breakdown'
    },
    {
      // Operations, kept apart from the two money tabs on purpose. Mixing them would put
      // two different "Orders" numbers on one screen with nothing explaining the gap.
      id: 'connector',
      content: 'Connector Performance',
      panelID: 'connector-panel',
      accessibilityLabel: 'View retailer connector operations — orders received, acceptance and tracking SLA'
    },
  ];


  // Render performance overview tab with error boundary
  const renderOverviewTab = () => {
    try {
      return (
        <Layout>
          {/* Key Metrics Cards */}
          <Layout.Section>
            <Grid>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <OrdersCard
                  title="Orders"
                  tooltip="All fulfilled orders counted for billing in this period. This is a billing figure — the Connector tab counts orders received from retailers, which will be a different number because rejected, cancelled and in-flight orders never become billable."
                  value={performanceMetrics?.totalOrders || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`AOV: ${(() => {
                  //   try {
                  //     return apiClient.formatCurrency(performanceMetrics?.averageOrderValue || 0);
                  //   } catch (error) {
                  //     return '$0.00';
                  //   }
                  // })()}`}
                  icon={OrderIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <RevenueCard
                  title="Gross Revenue"
                  value={performanceMetrics?.totalGrossRevenue || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  format="currency"
                  tooltip={"Total revenue generated across all brands in the selected time period. Calculated from fulfilled orders."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <MetricCard
                  title="Discounts"
                  tooltip="Discounts applied in Shopify and deducted from gross to reach net sales. 2W does not import discounts from retailers — this is the brand's own figure."
                  value={performanceMetrics?.totalDiscounts || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="Total amount refunded"
                  icon={ReceiptRefundIcon}
                  color="critical"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <MetricCard
                  title="Refunds"
                  tooltip="Refunds issued in this period, including refunds for orders placed earlier. Commission is reduced accordingly, so a refund reduces net sales in the period the refund lands, not the period the order did."
                  value={performanceMetrics?.totalRefunds || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="Total amount refunded"
                  icon={ReceiptRefundIcon}
                  color="critical"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <RevenueCard
                  title="Net Sales"
                  value={performanceMetrics?.totalRevenue || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => apiClient.formatCurrency(value)}
                  tooltip={"Total sales after excluding discounts and refunds."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <CommissionCard
                  title="Marketplace Commission"
                  value={performanceMetrics?.commissionBreakdown?.baseMarketplaceCommission || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => apiClient.formatCurrency(value)}
                  tooltip="Marketplace commission from fulfilled orders (before adjustments)"
                  icon={CashDollarFilledIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <CommissionCard
                  title="2W Commission"
                  value={performanceMetrics?.commissionBreakdown?.base2wCommission || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => apiClient.formatCurrency(value)}
                  tooltip="2W service commission from fulfilled orders (before adjustments)"
                  icon={CashDollarFilledIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <MetricCard
                  title="Commission Adjustments"
                  value={performanceMetrics?.commissionBreakdown?.totalAdjustmentAmount || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => {
                    try {
                      const prefix = value >= 0 ? '+' : '';
                      return `${prefix}${apiClient.formatCurrency(value)}`;
                    } catch (error) {
                      return `$${(value || 0).toFixed(2)}`;
                    }
                  }}
                  tooltip="Manual commission adjustments (additions and deductions) applied across all brands."
                  icon={CashDollarFilledIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <MetricCard
                  title="Shipping Fees"
                  tooltip="Shipping fees 2W charged the brand, from the configured weight-based rates per service level. This is what we bill, not what fulfilment cost us — there is no cost feed."
                  value={performanceMetrics?.totalShippingFees || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`${performanceMetrics?.totalMarketplaces || 0} marketplaces`}
                  icon={StoreIcon}
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <MetricCard
                  title="Return Fees"
                  tooltip="Return fees charged to the brand, from the configured default rate plus any per-retailer override."
                  value={performanceMetrics?.totalReturnFees || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => (() => {
                    try {
                      return apiClient.formatCurrency(value);
                    } catch (error) {
                      return '$0.00';
                    }
                  })()}
                  // change={{
                  //   percentage: 0,
                  //   direction: 'neutral',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle="From refund processing"
                  icon={ReturnIcon}
                  color="warning"
                />
              </Grid.Cell>

              <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <RevenueCard
                  title="Net Payout"
                  value={performanceMetrics?.totalPayout || 0}
                  loading={detailedLoading}
                  error={detailedError}
                  formatter={(value) => apiClient.formatCurrency(value)}
                  tooltip={"Total payout merchant will get after all discounts, refunds, commission, returns and shipping fees."}
                  // change={performanceMetrics && {
                  //   percentage: performanceMetrics.monthlyGrowth,
                  //   direction: performanceMetrics.monthlyGrowth >= 0 ? 'up' : 'down',
                  //   period: `vs. previous ${timeFilter} days`,
                  // }}
                  // subtitle={`Across ${performanceMetrics?.activeBrandsCount || 0} brands`}
                  icon={CashDollarIcon}
                />
              </Grid.Cell>
            </Grid>
          </Layout.Section>

        </Layout>
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error rendering overview tab:', error);
      }
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to display performance overview. Please refresh the page.</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }
  };

  // Render marketplace analysis tab with error boundary
  const renderMarketplaceTab = () => {
    try {
      return (
        <Layout>
          <Layout.Section>
            {/* onMarketplaceSelect is deliberately NOT passed. It used to be a handler whose only
                body was a dev-mode console.log, i.e. `() => {}` in production - every row
                advertised a marketplace drill-down view that does not exist.
                MarketplaceBreakdown calls it optionally (`onMarketplaceSelect?.(item)`), so the
                rows no longer offer the affordance. Building the real drill-down would use
                apiClient.getMarketplaceAnalytics / getOrderMarketplaceBreakdown, which exist. */}
            <MarketplaceBreakdown
              brandId={brandFilter.join(',') || null}
              period={analyticsParams.days || 30}
              dateRange={analyticsParams.startDate && analyticsParams.endDate ? {
                startDate: analyticsParams.startDate,
                endDate: analyticsParams.endDate
              } : null}
              title="Cross-Brand Marketplace Analysis"
              showFilters={true}
            />
          </Layout.Section>
        </Layout>
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics: Error rendering marketplace tab:', error);
      }
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to display marketplace analysis. Please refresh the page.</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }
  };



  // Global loading and error state
  if (overviewError) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load analytics overview. {overviewError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (brandsError && !brands.length) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load brands data. {brandsError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (brandsLoading || overviewLoading) {
    return (
      <Page title="Analytics" backAction={{ content: 'Dashboard', url: '/' }} fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading analytics data...
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Fetching cross-brand performance metrics
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Cross-Brand Analytics"
      fullWidth
      subtitle={`Performance insights across ${totalBrandCount} brand${totalBrandCount !== 1 ? 's' : ''}`}
      backAction={{ content: 'Dashboard', url: '/' }}
      compactTitle
    >
      {detailedError && (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>{detailedError.message || 'Failed to load analytics data'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        {/* Filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="400" align="start" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={CalendarIcon} />
                  {/* Popover + ActionList, not a Select — a native <select> emits no
                      change event for the row that is already selected, so once a custom
                      range was applied the row that opens the picker could not be clicked
                      again. Every row here is a button. Mirrors the brand Analytics tab. */}
                  <Popover
                    active={periodPopoverActive}
                    onClose={() => setPeriodPopoverActive(false)}
                    activator={(
                      <Button onClick={() => setPeriodPopoverActive((v) => !v)} disclosure>
                        {timeFilter === 'custom'
                          ? formatCustomDateLabel()
                          : (timeFilterOptions.find((o) => o.value === timeFilter)?.label ?? 'Select period')}
                      </Button>
                    )}
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={[
                        ...timeFilterOptions.map((opt) => ({
                            content: opt.label,
                            active: timeFilter === opt.value,
                            onAction: () => {
                              setPeriodPopoverActive(false);
                              handleTimeFilterChange(opt.value);
                            },
                          })),
                        {
                          content: timeFilter === 'custom'
                            ? formatCustomDateLabel()
                            : 'Custom Date Range',
                          active: timeFilter === 'custom',
                          onAction: () => {
                            setPeriodPopoverActive(false);
                            setShowCustomDateModal(true);
                          },
                        },
                      ]}
                    />
                  </Popover>
                </InlineStack>

                <InlineStack gap="200" blockAlign="center">
                  <Icon source={FilterIcon} />
                  <Popover
                    active={brandPickerOpen}
                    activator={(
                      <Button disclosure onClick={() => setBrandPickerOpen((o) => !o)}>
                        {brandFilter.length === 0
                          ? 'All brands'
                          : brandFilter.length === 1
                            ? (brands.find((b) => b?.id === brandFilter[0])?.name ?? '1 brand')
                            : `${brandFilter.length} brands`}
                      </Button>
                    )}
                    onClose={() => setBrandPickerOpen(false)}
                  >
                    <Box padding="300" minWidth="240px">
                      <BlockStack gap="200">
                        <ChoiceList
                          allowMultiple
                          title="Brands"
                          titleHidden
                          choices={brandFilterOptions.map((o) => ({ label: o.label, value: o.value }))}
                          selected={brandFilter}
                          onChange={setBrandFilter}
                        />
                        {brandFilter.length > 0 && (
                          <Button variant="plain" onClick={() => setBrandFilter([])}>
                            Clear — show all brands
                          </Button>
                        )}
                      </BlockStack>
                    </Box>
                  </Popover>
                </InlineStack>

                <Text variant="bodySm" tone="subdued">
                  Showing data for {
                    timeFilter === '999999' ? 'all time' :
                      timeFilter === 'custom' ? formatCustomDateLabel() :
                        timeFilter === 'mtd' ? 'month to date' :
                          timeFilter === 'ytd' ? 'year to date' :
                            timeFilter === 'this_month' ? 'this month' :
                              timeFilter === 'last_month' ? 'last month' :
                                `last ${timeFilter} days`
                  }
                  {brandFilter.length > 0 && (() => {
                    // Name them while the list is short enough to read; past that a
                    // count is more use than a wrapped wall of names.
                    const names = brandFilter
                      .map((id) => brands?.find?.((b) => b?.id === id)?.name)
                      .filter(Boolean);
                    if (names.length === 0) return ` • ${brandFilter.length} selected`;
                    return names.length <= 3
                      ? ` • ${names.join(', ')}`
                      : ` • ${names.length} brands`;
                  })()}
                </Text>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Tabs */}
        <Layout.Section>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
            <Box paddingBlockStart="600">
              {selectedTab === 0 && renderOverviewTab()}
              {selectedTab === 1 && renderMarketplaceTab()}
              {selectedTab === 2 && (
                <ConnectorPerformance
                  // analyticsParams.days, not timeFilter. timeFilter carries preset
                  // names — 'mtd', 'ytd', 'this_month', 'last_month', 'custom' — and
                  // sending those where a day count is expected meant five of the eight
                  // presets silently fell back to the API default instead of the range
                  // the admin picked. The Marketplace tab already passes the resolved
                  // number; this one was the odd case out.
                  days={analyticsParams.days || 30}
                  brandId={brandFilter.join(',') || null}
                />
              )}
            </Box>
          </Tabs>
        </Layout.Section>
      </Layout>

      {/* Custom Date Range Modal */}
      <Modal
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        title="Select Custom Date Range"
        primaryAction={{
          content: 'Apply Date Range',
          onAction: handleCustomDateSubmit,
          disabled: !customStartDate || !customEndDate,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowCustomDateModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Text variant="bodyMd" tone="subdued">
              Select a custom date range for analytics data. The range will be applied to all metrics and charts.
            </Text>
            <FormLayout.Group>
              <TextField
                label="Start Date"
                type="date"
                value={customStartDate}
                onChange={setCustomStartDate}
                helpText="Select the start date for your analysis"
              />
              <TextField
                label="End Date"
                type="date"
                value={customEndDate}
                onChange={setCustomEndDate}
                helpText="Select the end date for your analysis"
                min={customStartDate}
              />
            </FormLayout.Group>
            {customStartDate && customEndDate && (
              <Box padding="300" background="bg-fill-info-secondary" borderRadius="200">
                <Text variant="bodySm">
                  Selected range: <Text variant="bodySm" fontWeight="medium">{formatCustomDateLabel()}</Text>
                </Text>
              </Box>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}